import { type ReactNode, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { AdminIcon } from "@/components/admin/icon";
import { Markdown } from "@/components/admin/markdown";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import type { OnText } from "@/lib/ai/ai-api";

interface AiAssistProps<T> {
	/** Libellé du bouton déclencheur. */
	label: string;
	/** Titre affiché en tête du panneau d'aperçu. */
	panelTitle: string;
	/** Appel API streaming : `onText` reçoit les fragments, la promesse résout avec le résultat. */
	run: (onText: OnText) => Promise<T>;
	/** Rendu de la proposition avant application. */
	renderPreview: (result: T) => ReactNode;
	/** Appliquée quand l'utilisateur valide la proposition. */
	onApply: (result: T) => void;
	/** Désactive le bouton (ex. champs requis manquants). */
	disabled?: boolean;
	size?: "sm" | "md";
}

const MARKER_LABELS: Record<string, string> = {
	TITRE: "Titre",
	COURTE: "Description courte",
	LONGUE: "Description longue",
	METATITRE: "Titre meta",
	METADESC: "Description meta",
	TAGS: "Tags",
};

/** Transforme les marqueurs [XXX] en titres Markdown pour un stream formaté. */
function prettify(text: string): string {
	return text
		.replace(
			/\[([A-ZÉ]+)\]/g,
			(_, m: string) => `\n\n### ${MARKER_LABELS[m] ?? m}\n\n`,
		)
		.trimStart();
}

/**
 * Bouton « ✨ Assistant IA » générique : génère une proposition (affichée en
 * streaming), puis l'applique seulement si l'utilisateur valide (jamais
 * d'écrasement silencieux du travail en cours).
 */
export function AiAssist<T>({
	label,
	panelTitle,
	run,
	renderPreview,
	onApply,
	disabled,
	size = "sm",
}: AiAssistProps<T>) {
	const [loading, setLoading] = useState(false);
	const [streamText, setStreamText] = useState("");
	const [result, setResult] = useState<T | null>(null);
	const [err, setErr] = useState<string | null>(null);

	const generate = async () => {
		setErr(null);
		setResult(null);
		setStreamText("");
		setLoading(true);
		try {
			const r = await run((delta) => setStreamText((t) => t + delta));
			setResult(r);
		} catch (e) {
			setErr(extractErrorMessage(e, "Échec de la génération par l'IA."));
		} finally {
			setLoading(false);
		}
	};

	const apply = () => {
		if (result !== null) onApply(result);
		setResult(null);
		setStreamText("");
	};

	const close = () => {
		setResult(null);
		setStreamText("");
	};

	return (
		<div style={{ margin: "8px 0" }}>
			<AdminButton
				variant="ghost"
				size={size}
				onClick={generate}
				disabled={disabled || loading}
			>
				<AdminIcon n={loading ? "refresh" : "star"} s={14} />
				{loading ? "Génération…" : label}
			</AdminButton>

			{err && (
				<div
					style={{
						marginTop: 8,
						padding: "8px 10px",
						background: "var(--a-danger-soft, #fdecea)",
						color: "var(--a-danger)",
						borderRadius: 6,
						fontSize: 12,
						display: "flex",
						gap: 8,
						alignItems: "flex-start",
					}}
				>
					<AdminIcon n="alert" s={14} />
					<span>{err}</span>
				</div>
			)}

			{/* Aperçu en direct pendant la génération */}
			{loading && streamText && (
				<div
					style={{
						marginTop: 10,
						border: "1px solid var(--a-border)",
						borderRadius: 8,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							padding: "8px 12px",
							background: "var(--a-surface-2)",
							fontSize: 12,
							fontWeight: 600,
							color: "var(--a-ink-soft)",
						}}
					>
						<AdminIcon n="refresh" s={13} />
						Génération en cours…
					</div>
					<div
						style={{
							padding: 12,
							maxHeight: 240,
							overflow: "auto",
							background: "var(--a-surface)",
						}}
					>
						<Markdown>{prettify(streamText)}</Markdown>
						<span style={{ opacity: 0.5 }}>▍</span>
					</div>
				</div>
			)}

			{/* Proposition finale à valider */}
			{result !== null && (
				<div
					style={{
						marginTop: 10,
						border: "1px solid var(--a-terra-soft, var(--a-border))",
						borderRadius: 8,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							padding: "8px 12px",
							background: "var(--a-terra-soft, var(--a-surface-2))",
							color: "var(--a-terra-deep, var(--a-ink))",
							fontSize: 12,
							fontWeight: 600,
						}}
					>
						<AdminIcon n="star" s={13} />
						{panelTitle}
						<span style={{ marginLeft: "auto", fontWeight: 400, opacity: 0.7 }}>
							Proposition — à valider
						</span>
					</div>
					<div
						style={{
							padding: 12,
							fontSize: 12.5,
							background: "var(--a-surface)",
						}}
					>
						{renderPreview(result)}
					</div>
					<div
						style={{
							display: "flex",
							gap: 8,
							justifyContent: "flex-end",
							padding: "8px 12px",
							borderTop: "1px solid var(--a-border)",
						}}
					>
						<AdminButton variant="ghost" size="sm" onClick={close}>
							Ignorer
						</AdminButton>
						<AdminButton
							variant="ghost"
							size="sm"
							onClick={generate}
							disabled={loading}
						>
							<AdminIcon n="refresh" s={12} />
							Régénérer
						</AdminButton>
						<AdminButton variant="primary" size="sm" onClick={apply}>
							<AdminIcon n="check" s={12} />
							Appliquer
						</AdminButton>
					</div>
				</div>
			)}
		</div>
	);
}
